import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SuperAdminService from '../../services/SuperAdminService';

// ============================================================================
// ŞİRKET OLUŞTURMA DİYALOĞU (superadmin + bayi)
// POST /api/superadmin/companies ucunu çağırır (AdminScope). Bayi çağırırsa
// şirket backend'de otomatik o bayiye atanır — DealerId istemciden gönderilmez.
// ============================================================================

interface CreateCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  /** Şirket başarıyla oluşturulduğunda çağrılır (liste yenileme için). */
  onCreated: () => void;
}

const EMPTY_FORM = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
};

const CreateCompanyDialog: React.FC<CreateCompanyDialogProps> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    if (saving) return;
    setForm(EMPTY_FORM);
    setError(null);
    setShowPassword(false);
    onClose();
  };

  const isValid =
    form.companyName.trim().length > 0 &&
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(form.email.trim()) &&
    form.password.length >= 6;

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    try {
      setSaving(true);
      setError(null);
      await SuperAdminService.getInstance().createCompany({
        companyName: form.companyName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
      });
      setForm(EMPTY_FORM);
      setShowPassword(false);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Şirket oluşturulurken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          <Typography variant="h6" component="span">
            Yeni Şirket Oluştur
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Şirket Adı"
          value={form.companyName}
          onChange={setField('companyName')}
          sx={{ mb: 2 }}
          autoFocus
        />

        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Yönetici (Admin) Hesabı
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField fullWidth label="Ad" value={form.firstName} onChange={setField('firstName')} />
          <TextField fullWidth label="Soyad" value={form.lastName} onChange={setField('lastName')} />
        </Box>
        <TextField
          fullWidth
          label="E-posta"
          type="email"
          value={form.email}
          onChange={setField('email')}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Şifre"
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={setField('password')}
          helperText="En az 6 karakter"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          label="Telefon (opsiyonel)"
          value={form.phone}
          onChange={setField('phone')}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <BusinessIcon />}
        >
          {saving ? 'Oluşturuluyor...' : 'Şirket Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCompanyDialog;
